
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as SonnerToaster } from 'sonner';
import { AuthProvider } from '@/contexts/AuthContext';
import { CartProvider } from '@/contexts/CartContext';
import { FavoritesProvider } from '@/contexts/FavoritesContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { ScrollToTop } from '@/components/ScrollToTop';

// Pages
import Index from '@/pages/Index';
import Home from '@/pages/Home';
import Shop from '@/pages/Shop';
import ShopByCategory from '@/pages/ShopByCategory';
import ProductPage from '@/pages/ProductPage';
import AboutUs from '@/pages/AboutUs';
import Contact from '@/pages/Contact';
import Auth from '@/pages/Auth';
import AdminLogin from '@/pages/AdminLogin';
import MyAccount from '@/pages/MyAccount';
import Checkout from '@/pages/Checkout';
import OrderSuccess from '@/pages/OrderSuccess';
import UnifiedAdminDashboard from '@/components/admin/UnifiedAdminDashboard';
import NotFound from '@/pages/NotFound';
import ResetPassword from '@/pages/ResetPassword';
import CategoriesPage from '@/pages/CategoriesPage';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN') {
        // Refresh query cache when user signs in
        queryClient.invalidateQueries();
      } else if (event === 'PASSWORD_RECOVERY') {
        // A recovery session was established from the email link. Make sure the
        // user lands on the reset-password page regardless of which URL the
        // recovery email redirected to (e.g. the dashboard "Send recovery"
        // uses the Site URL). The recovery session persists across the reload.
        if (window.location.pathname !== '/reset-password') {
          window.location.replace('/reset-password');
        }
      } else if (event === 'SIGNED_OUT') {
        // Clear query cache when user signs out
        queryClient.clear();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
      <LanguageProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <CartProvider>
              <FavoritesProvider>
              <Router>
                <ScrollToTop />
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/home" element={<Home />} />
                  <Route path="/categories" element={<CategoriesPage />} />
                  <Route path="/shop" element={<Shop />} />
                  <Route path="/shop/category/:categoryId" element={<ShopByCategory />} />
                  <Route path="/shop/subcategory/:subcategoryId" element={<ShopByCategory />} />
                  <Route path="/product/:id" element={<ProductPage />} />
                  <Route path="/about-us" element={<AboutUs />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/my-account" element={<MyAccount />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/order-success" element={<OrderSuccess />} />
                  <Route path="/dashboard" element={<UnifiedAdminDashboard />} />
                  <Route path="/users-dashboard" element={<UnifiedAdminDashboard />} />
                  <Route path="/products-dashboard" element={<UnifiedAdminDashboard />} />
                  <Route path="/orders-dashboard" element={<UnifiedAdminDashboard />} />
                  <Route path="/category-dashboard" element={<UnifiedAdminDashboard />} />
                  <Route path="/brands-dashboard" element={<UnifiedAdminDashboard />} />
                  <Route path="/locations-dashboard" element={<UnifiedAdminDashboard />} />
                  <Route path="/stores-dashboard" element={<UnifiedAdminDashboard />} />
                  <Route path="/404" element={<NotFound />} />
                  <Route path="*" element={<Navigate to="/404" replace />} />
                </Routes>
              </Router>
              <Toaster />
              <SonnerToaster 
                theme="dark" 
                position="top-right" 
                richColors 
                closeButton
                duration={4000}
              />
            </FavoritesProvider>
          </CartProvider>
        </AuthProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
